export const MANDATORY_SOURCES = new Set(['sphere_10', 'cold_call_54', 'door_knocking_11']);

export function pragueTomorrow(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Prague', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = (type) => parts.find((p) => p.type === type).value;
  const date = new Date(`${part('year')}-${part('month')}-${part('day')}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

export const isoDate = (value) => String(value || '').slice(0, 10);
export const samePolicy = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Version identifiers are opaque; server append order resolves same-day versions.
export const latestPolicyVersion = (versions) => versions.at(-1) ?? null;
export const activePolicyVersion = (versions, today) =>
  versions.filter((version) => isoDate(version.effective_from) <= today).at(-1) ?? null;
export const canCreateCatalogueItem = (definition) => definition.allowCreate === true;

export function policyChanges(before, after, path = '') {
  if (samePolicy(before, after)) return [];
  if (before && after && typeof before === 'object' && typeof after === 'object') {
    return [...new Set([...Object.keys(before), ...Object.keys(after)])].flatMap((key) =>
      policyChanges(before[key], after[key], path ? `${path}.${key}` : key));
  }
  return [{ path, before, after }];
}

export function needsJchConsent(before, after, role) {
  if (role !== 'JR') return false;
  return (after.catalogues?.sources || []).some((source) => {
    const old = before.catalogues?.sources?.find((item) => item.code === source.code);
    return !old || old.actionsPerContact !== source.actionsPerContact;
  });
}

const validDate = (date) => /^\d{4}-\d{2}-\d{2}$/.test(date || '')
  && !Number.isNaN(Date.parse(`${date}T12:00:00Z`))
  && new Date(`${date}T12:00:00Z`).toISOString().slice(0, 10) === date;

export function validatePolicyDraft(draft, groups, effectiveFrom, minimumDate) {
  if (!validDate(effectiveFrom) || effectiveFrom < minimumDate) {
    return 'Platnost musí začínat nejdříve zítra podle času Europe/Prague.';
  }
  for (const group of groups || []) {
    for (const field of group.fields || []) {
      const value = draft[group.key]?.[field.key];
      if (typeof value !== 'number' || !Number.isFinite(value)
        || (field.min != null && value < field.min) || (field.max != null && value > field.max)
        || (field.step === 1 && !Number.isInteger(value))) {
        return `Zkontrolujte číselnou hodnotu „${field.label}“ ve skupině „${group.title}“.`;
      }
    }
  }
  if (Number(draft.trafficLight?.orangeFrom) >= Number(draft.trafficLight?.greenFrom)) {
    return 'Oranžová hranice musí být nižší než zelená.';
  }
  for (const [key, items] of Object.entries(draft.catalogues || {})) {
    const codes = new Set();
    for (const item of items) {
      if (!/^[a-z][a-z0-9_]{0,63}$/.test(item.code) || codes.has(item.code) || !item.name?.trim() || item.name.length > 120) {
        return `Číselník ${key}: vyplňte název a jedinečný kód (malá písmena, číslice, podtržítko).`;
      }
      codes.add(item.code);
      if (key === 'sources') {
        if (item.mandatory && !MANDATORY_SOURCES.has(item.code)) {
          return 'Povinnými mohou být pouze sféra vlivu, studené volání a doorknocking.';
        }
        if (!item.unit?.trim() || item.unit.length > 80 || !Number.isInteger(item.actionsPerContact)
          || item.actionsPerContact < 1 || item.actionsPerContact > 1000000
          || !Number.isInteger(item.weeklyVolume) || item.weeklyVolume < 0 || item.weeklyVolume > 1000000
          || !Number.isFinite(item.meetingConversion) || item.meetingConversion < 0 || item.meetingConversion > 1) {
          return `Zdroj „${item.name}“: zkontrolujte jednotku, počet akcí, týdenní objem a konverzi v rozsahu 0–1.`;
        }
      }
    }
  }
  const dates = new Set();
  for (const holiday of draft.calendar?.holidays || []) {
    if (!validDate(holiday.date) || !holiday.name?.trim() || dates.has(holiday.date)) {
      return 'U svátků vyplňte jedinečné platné datum a název.';
    }
    dates.add(holiday.date);
  }
  for (const period of draft.calendar?.quietPeriods || []) {
    if (!period.name?.trim() || !validDate(period.from) || !validDate(period.to) || period.from > period.to) {
      return 'U tichých období vyplňte název a platná data od–do (od nesmí být po do).';
    }
  }
  return '';
}
